import multiApiClient from "./base";
import { ottoItemsApi, OttoMarginRulesApi } from "./otto";
import { PackageMappingsApi } from "./package_mappings";

export const RepricerArticlesApi = {
  // ── Base articles ────────────────────────────────────────────────────────
  getArticles: ({ filters, orders, limit = 100, offset = 0, details } = {}) =>
    multiApiClient.get("/articles/", {
      params: {
        ...(filters ? { filters: JSON.stringify(filters) } : {}),
        ...(orders ? { orders: JSON.stringify(orders) } : {}),
        limit,
        offset,
        ...(details ? { details } : {}),
      },
    }),

  getArticleStats: (details = null) =>
    multiApiClient.get("/articles/stats/summary", {
      params: details ? { details } : {},
    }),

  /** Sales/profit KPIs for one SKU (default last 7 days). */
  getArticleSalesStats: (
    sku,
    { days = 7, channel = "all", from_date, to_date } = {},
  ) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/stats/sales`, {
      params: {
        ...(from_date && to_date ? { from_date, to_date } : { days }),
        channel,
      },
    }),

  /** Batch sales/profit KPIs for table rows (comma-separated skus). */
  getArticlesSalesStats: ({
    skus,
    days = 7,
    channel = "all",
    from_date,
    to_date,
  } = {}) =>
    multiApiClient.get("/articles/stats/sales", {
      params: {
        skus: Array.isArray(skus) ? skus.join(",") : skus,
        ...(from_date && to_date ? { from_date, to_date } : { days }),
        channel,
      },
    }),

  /**
   * Analysis table: articles + period KPIs with global sort/filter/pagination.
   * KPI sorts (retour_qty, period_profit, …) are applied across all matching articles.
   */
  getArticlesAnalytics: ({
    filters,
    kpi_filters,
    orders,
    days = 30,
    channel = "all",
    from_date,
    to_date,
    limit = 25,
    offset = 0,
  } = {}) =>
    multiApiClient.get("/articles/analytics", {
      params: {
        ...(filters ? { filters: JSON.stringify(filters) } : {}),
        ...(kpi_filters ? { kpi_filters: JSON.stringify(kpi_filters) } : {}),
        ...(orders ? { orders: JSON.stringify(orders) } : {}),
        ...(from_date && to_date ? { from_date, to_date } : { days }),
        channel,
        limit,
        offset,
      },
    }),

  /** Main/Stock expand: article KPIs + bundles that consume this SKU's stock. */
  getStockConsumerStats: (
    sku,
    { days = 30, channel = "all", from_date, to_date } = {},
  ) =>
    multiApiClient.get(
      `/articles/${encodeURIComponent(sku)}/stats/stock-consumers`,
      {
        params: {
          ...(from_date && to_date ? { from_date, to_date } : { days }),
          channel,
        },
      },
    ),

  getArticle: (sku, details = null) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}`, {
      params: details ? { details } : {},
    }),

  createArticle: (data) => multiApiClient.post("/articles/", data),

  updateArticle: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}`, data),

  deleteArticle: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}`),

  // ── Shopify details ──────────────────────────────────────────────────────
  getShopifyDetails: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/shopify`),

  createShopifyDetails: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/shopify`, data),

  updateShopifyDetails: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}/shopify`, data),

  deleteShopifyDetails: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/shopify`),

  // ── FBM details ──────────────────────────────────────────────────────────
  getFbmDetails: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/fbm`),

  createFbmDetails: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/fbm`, data),

  updateFbmDetails: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}/fbm`, data),

  updateFbmDetailsById: (fbmId, data) =>
    multiApiClient.put(`/articles/fbm/${fbmId}`, data),

  deleteFbmDetails: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/fbm`),

  // ── Kaufland details ─────────────────────────────────────────────────────
  getKauflandDetails: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/kaufland`),

  createKauflandDetails: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/kaufland`, data),

  updateKauflandDetails: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}/kaufland`, data),

  deleteKauflandDetails: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/kaufland`),

  // ── Otto details ─────────────────────────────────────────────────────────
  getOttoDetails: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/otto`),

  createOttoDetails: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/otto`, data),

  updateOttoDetails: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}/otto`, data),

  deleteOttoDetails: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/otto`),

  // ── FBA details ──────────────────────────────────────────────────────────
  getFbaDetails: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/fba`),

  createFbaDetails: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/fba`, data),

  updateFbaDetails: (sku, data) =>
    multiApiClient.put(`/articles/${encodeURIComponent(sku)}/fba`, data),

  deleteFbaDetails: (sku) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/fba`),

  // ── Pricing history ──────────────────────────────────────────────────────
  getPricingHistory: (sku) =>
    multiApiClient.get(`/articles/${encodeURIComponent(sku)}/pricing-history`),

  createPricingHistory: (sku, data) =>
    multiApiClient.post(
      `/articles/${encodeURIComponent(sku)}/pricing-history`,
      data,
    ),

  deletePricingHistory: (sku, historyId) =>
    multiApiClient.delete(
      `/articles/${encodeURIComponent(sku)}/pricing-history/${historyId}`,
    ),

  // ── Cron ─────────────────────────────────────────────────────────────────
  startCronJob: (
    schedule = "0 8 * * *",
    update_prices = true,
    discord_webhook = null,
  ) =>
    multiApiClient.post("/articles/cron/start", null, {
      params: {
        schedule,
        update_prices,
        ...(discord_webhook ? { discord_webhook } : {}),
      },
    }),

  stopCronJob: () => multiApiClient.post("/articles/cron/stop"),

  getCronStatus: () => multiApiClient.get("/articles/cron/status"),

  // ── Sync ──────────────────────────────────────────────────────────────────
  syncArticles: (sku, update_prices = true) =>
    multiApiClient.post(`/articles/sync/${encodeURIComponent(sku)}`, null, {
      params: { update_prices },
    }),

  // ── UVP ──────────────────────────────────────────────────────────────────
  setUVP: (sku, data) =>
    multiApiClient.post(`/articles/${encodeURIComponent(sku)}/uvp`, data),

  removeUVP: (sku, data = {}) =>
    multiApiClient.delete(`/articles/${encodeURIComponent(sku)}/uvp`, {
      data,
    }),

  /** Clear penner/renner type + pricing history, restore min/max rules, recalculate. */
  resetPennerRenner: (sku) =>
    multiApiClient.post(
      `/articles/${encodeURIComponent(sku)}/reset-penner-renner`,
    ),
};

const _notImpl = (name) => () =>
  Promise.reject(new Error(`${name} is not implemented in the new API`));

// Backward-compatibility shim — used by RepricerManagementSubTab, MarginRulesSubTab, PackageMappingsSubTab
export const OttoRepricerApi = {
  // Cron
  getCronStatus: () => ottoItemsApi.getCronStatus(),
  startCronJob: (schedule, webhook) =>
    ottoItemsApi.startCronJob(schedule, webhook),
  stopCronJob: () => ottoItemsApi.stopCronJob(),
  updateCronSchedule: _notImpl("updateCronSchedule"),
  // XLSX
  checkXlsxExists: () => ottoItemsApi.checkXlsxExists(),
  uploadXlsx: (file) => ottoItemsApi.uploadXlsx(file),
  downloadXlsx: () => ottoItemsApi.downloadXlsx(),
  // Workflow
  checkWorkflowResultExists: () => ottoItemsApi.checkWorkflowResultExists(),
  downloadWorkflowResult: () => ottoItemsApi.downloadWorkflowResult(),
  runRepricerWorkflow: _notImpl("runRepricerWorkflow"),
  recalculatePricesDb: _notImpl("recalculatePricesDb"),
  // Margin rules
  getMarginRules: () => OttoMarginRulesApi.getMarginRules(),
  getMarginRule: (id) => OttoMarginRulesApi.getMarginRule(id),
  createMarginRule: (rule) => OttoMarginRulesApi.createMarginRule(rule),
  updateMarginRule: (id, rule) => OttoMarginRulesApi.updateMarginRule(id, rule),
  deleteMarginRule: (id) => OttoMarginRulesApi.deleteMarginRule(id),
  // Package mappings
  getPackageMappings: () => PackageMappingsApi.getPackageMappings(),
  createPackageMapping: (data) => PackageMappingsApi.createPackageMapping(data),
  updatePackageMapping: (id, data) =>
    PackageMappingsApi.updatePackageMapping(id, data),
  deletePackageMapping: (id) => PackageMappingsApi.deletePackageMapping(id),
};
